import { APP_CURRENCY } from './constants/currency';

export const APP_NUMBER_LOCALE = APP_CURRENCY.locale;

export type FormatDecimalOptions = {
  useGrouping?: boolean;
  trimTrailingZeros?: boolean;
};

/**
 * Formatea un número con locale es-BO: 1.500,44
 */
export const formatDecimal = (
  value: number | string | null | undefined,
  decimalPlaces = 2,
  options?: FormatDecimalOptions,
): string => {
  if (value == null || value === '') return '';

  const numericValue = typeof value === 'number' ? value : parseLocaleDecimal(String(value));

  if (numericValue == null || !Number.isFinite(numericValue)) return '';

  const useGrouping = options?.useGrouping ?? true;
  const trimTrailingZeros = options?.trimTrailingZeros ?? false;

  return numericValue.toLocaleString(APP_NUMBER_LOCALE, {
    minimumFractionDigits: trimTrailingZeros ? 0 : decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping,
  });
};

/**
 * Valor editable sin separadores de miles: 1500,44
 */
export const formatDecimalForEditing = (
  value: number | string | null | undefined,
  decimalPlaces = 2,
): string => {
  if (value == null || value === '') return '';

  const numericValue = typeof value === 'number' ? value : parseLocaleDecimal(String(value));

  if (numericValue == null || !Number.isFinite(numericValue)) return '';

  if (decimalPlaces === 0) {
    return String(Math.trunc(numericValue));
  }

  return String(numericValue).replace('.', ',');
};

/**
 * Parsea entrada con coma o punto como separador decimal.
 * Acepta: 130,44 | 130.44 | 1.500,44 | 1.500 | 130,
 */
export const parseLocaleDecimal = (value: string): number | null => {
  const trimmed = value.replace(/\s/g, '');
  if (trimmed === '' || trimmed === ',' || trimmed === '.') return null;

  const commaIndex = trimmed.indexOf(',');
  const lastDotIndex = trimmed.lastIndexOf('.');

  if (commaIndex !== -1) {
    const integerPart = trimmed.slice(0, commaIndex).replace(/\./g, '');
    const decimalPart = trimmed.slice(commaIndex + 1).replace(/[.,]/g, '');
    // "1,500" with 3+ fraction digits is thousands-style misuse → treat as integer 1500
    if (decimalPart.length > 2 && !trimmed.slice(0, commaIndex).includes('.')) {
      const asThousands = Number(`${integerPart}${decimalPart}`);
      return Number.isFinite(asThousands) ? asThousands : null;
    }
    const normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (lastDotIndex !== -1) {
    const afterDot = trimmed.slice(lastDotIndex + 1);
    const beforeDot = trimmed.slice(0, lastDotIndex).replace(/\./g, '');

    // Single dot + 1–2 digits → decimal (1.5 / 1.50)
    if (trimmed.indexOf('.') === lastDotIndex && /^\d{1,2}$/.test(afterDot)) {
      const normalized = `${beforeDot}.${afterDot}`;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    // Dots as thousands: 1.500 / 1.500.000
    const parsed = Number(trimmed.replace(/\./g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Sanitiza entrada mientras el usuario escribe (locale es-BO).
 * Preserva miles con punto (1.500) y decimal con coma (1.500,44 / 1500,5).
 */
export const sanitizeDecimalInput = (raw: string, maxDecimals: number): string => {
  const trimmed = raw.replace(/\s/g, '');
  if (!trimmed) return '';

  // Keep only digits, dots, commas
  const cleaned = trimmed.replace(/[^\d.,]/g, '');
  if (!cleaned) return '';

  const hasComma = cleaned.includes(',');

  if (hasComma) {
    const commaIndex = cleaned.indexOf(',');
    const integerRaw = cleaned.slice(0, commaIndex);
    // Keep thousand dots in integer only as digits for typing; strip non-digits except dots then remove dots
    const integerPart = integerRaw.replace(/\./g, '').replace(/[^\d]/g, '');
    const decimalPart = cleaned
      .slice(commaIndex + 1)
      .replace(/[^\d]/g, '')
      .slice(0, maxDecimals);
    if (cleaned.endsWith(',') && decimalPart.length === 0) {
      return `${integerPart},`;
    }
    return decimalPart.length > 0 ? `${integerPart},${decimalPart}` : integerPart;
  }

  // Only dots / digits — distinguish decimal vs thousands while typing
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (dotCount === 0) {
    return cleaned.replace(/[^\d]/g, '');
  }

  const lastDot = cleaned.lastIndexOf('.');
  const after = cleaned.slice(lastDot + 1);
  const before = cleaned.slice(0, lastDot).replace(/\./g, '');

  // Trailing dot while typing (e.g. "1.") — keep as pending decimal comma
  if (after.length === 0 && cleaned.endsWith('.')) {
    return `${before.replace(/[^\d]/g, '')},`;
  }

  // One dot + 1–2 digits → decimal form with comma
  if (dotCount === 1 && /^\d{1,2}$/.test(after)) {
    return `${before.replace(/[^\d]/g, '')},${after}`;
  }

  // Thousands (1.500 or 1.500.000): strip dots
  return cleaned.replace(/\./g, '').replace(/[^\d]/g, '');
};

export const clampDecimal = (value: number | null, min?: number, max?: number): number | null => {
  if (value == null) return null;

  let result = value;
  if (min != null && result < min) result = min;
  if (max != null && result > max) result = max;

  return result;
};

/**
 * Formatea peso en gramos: 4,50 g
 */
export const formatWeightGrams = (value: number | string | null | undefined): string => {
  const formatted = formatDecimal(value, 2, { trimTrailingZeros: true });
  return formatted ? `${formatted} g` : 'No especificado';
};

/**
 * Formatea largo en cm desde valor en cm.
 */
export const formatLengthCm = (value: number | string | null | undefined): string => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return 'No especificado';
  }

  const formatted = formatDecimal(value, 2, { trimTrailingZeros: true });
  return formatted ? `${formatted} cm` : 'No especificado';
};

/**
 * Formatea largo en cm desde valor en mm.
 */
export const formatLengthCmFromMm = (value: number | string | null | undefined): string => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return 'No especificado';
  }

  const cmValue = Number(value) / 10;
  return formatLengthCm(cmValue);
};
